<script setup lang="ts">
import { computed, ref } from 'vue'
import * as _ from 'lodash'

type ButtonVariant = 'primary' | 'secondary' | 'tertiary'
type ButtonSize = 'small' | 'large' | 'medium'
interface Props {
  type?: string
  title?: string
  variant?: ButtonVariant
  height?: string
  width?: string
  size?: ButtonSize
  prependIcon?: string
  active?: boolean
  disabled?: boolean
  tooltip?: string
  bgColor?: string
  activeBgColor?: string
  textColor?: string
  borderColor?: string
  textClass?: string
  loading?: boolean
  buttonClass?: string
}

const emit = defineEmits(['click'])

const props = withDefaults(defineProps<Props>(), {
  title: '',
  width: 'fit-content',
  type: 'button',
  variant: 'primary',
  size: 'medium',
  active: false,
  disabled: false,
  loading: false,
})

const marginLine = computed(() =>
  props.title.length === 0 ? 0 : 'calc(var(--v-btn-height) / -9) calc(var(--v-btn-height) / 4.5)',
)
const activeStatus = ref(false)
const activeBtn = computed(() => props.active || activeStatus.value)
const handleActive = (event: MouseEvent) => {
  emit('click', event)
}

const STYLES = {
  primary: {
    padding: { large: 'tw:p-16', medium: 'tw:p-12', small: 'tw:p-8' },
    normal: 'tw:bg-s-d-s-dark-background-brand-default',
    hover: 'tw:hover:bg-s-d-s-dark-background-brand-hover',
    active: 'tw:bg-s-d-s-dark-background-brand-hover',
    disabled: 'tw:bg-[#808080] tw:cursor-pointer',
    text: 'tw:text-s-d-s-dark-text-brand-primary',
    textDisabled: 'tw:text-s-d-s-dark-icon-disabled-default',
  },
  secondary: {
    padding: { large: 'tw:p-16', medium: 'tw:p-12', small: 'tw:p-8' },
    normal: 'tw:border tw:border-s-d-s-dark-text-default-tertiary/40',
    hover:
      'tw:hover:border-brand-mint-green tw:hover:bg-s-d-s-dark-background-brand-secondary-hover',
    active: 'tw:border-brand-mint-green tw:bg-s-d-s-dark-background-brand-secondary-hover',
    disabled:
      'tw:border tw:opacity-40 tw:border-s-d-s-dark-border-default-teriary tw:bg-s-d-s-dark-background-brand-secondary',
    text: 'tw:text-s-d-s-dark-text-default-primary',
    textHover: 'tw:hover:text-s-d-s-dark-text-brand-primary',
    textActive: 'tw:text-s-d-s-dark-text-brand-primary',
    textDisabled: 'tw:text-s-d-s-dark-text-default-primary',
  },
  tertiary: {
    padding: '' as const,
    normal: 'tw:py-[10px] tw:border-b tw:border-transparent tw:px-0 tw:bg-transparent',
    hover: 'tw:hover:border-s-d-s-dark-text-brand-secondary',
    active: 'tw:border-s-d-s-dark-text-brand-secondary',
    disabled: '',
    text: 'tw:text-s-d-s-dark-text-brand-secondary',
    textDisabled: 'tw:text-s-d-s-dark-text-disabled-default',
  },
} as const

const s = computed(() => STYLES[props.variant])

const getPadding = (): string => {
  if (props.variant === 'tertiary') return ''
  return _.get(s.value.padding, String(props.size), '')
}

const buttonClasses = computed(() => {
  const base = ['tw:rounded-radius-50', 'tw:transition-all', 'tw:duration-500']
  if (props.variant !== 'tertiary') {
    base.push(getPadding())
  }
  if (props.disabled) {
    base.push(s.value.disabled)
  } else {
    base.push(s.value.normal)
    if (activeBtn.value && 'active' in s.value) {
      base.push((s.value as any).active)
    }
  }

  return _.compact(_.flatten(base)).join(' ')
})

const textClasses = computed(() => {
  const sizeClass = props.size === 'small' ? 'tw:text-2' : 'tw:text-3'
  let color = props.disabled ? s.value.textDisabled : s.value.text
  if (!props.disabled && activeBtn.value && 'textActive' in s.value) {
    color = (s.value as any).textActive
  }
  return _.compact(_.flatten(['tw:leading-[1.2]', sizeClass, color])).join(' ')
})
</script>

<template>
  <div
    :style="{
      width: props.width,
      minWidth: props.width,
      height: props.height,
      minHeight: props.height,
    }"
    class="group"
  >
    <v-btn
      v-bind="$attrs"
      :type="props.type"
      :disabled="props.disabled"
      :loading="props.loading"
      width="100%"
      height="100%"
      variant="flat"
      density="compact"
      @click="handleActive"
      @mouseover="activeStatus = true"
      @mouseleave="activeStatus = false"
      class="tw:rounded-radius-50 tw:transition-all tw:duration-500"
      :class="[buttonClasses, buttonClass]"
    >
      <template v-slot:prepend>
        <slot name="prepend" :active="activeBtn">
          <v-icon v-if="props.prependIcon">
            {{ props.prependIcon }}
          </v-icon>
        </slot>
      </template>
      <template v-slot:default>
        <slot name="default">
          <span class="tw:leading-[1.2]" :class="[textClass, textClasses]">
            {{ props.title }}
          </span>
        </slot>
      </template>
    </v-btn>
    <v-tooltip v-if="props.tooltip && props.disabled" activator="parent" location="top">{{
      props.tooltip
    }}</v-tooltip>
  </div>
</template>

<style scoped lang="scss">
:deep(.v-btn__overlay) {
  background: transparent !important;
}

:deep(.v-btn__prepend) {
  margin-inline: v-bind(marginLine);
}

.custom-btn:not(.active) {
  border: var(--sds-size-stroke-border) solid v-bind('props.borderColor');
}

.custom-btn.active:not(.v-btn--disabled) {
  background-image: linear-gradient(90deg, #fff 50%, v-bind('props.activeBgColor') 50%);
  background-size: 200%;
  background-position: right;
  transition: background-position 0.4s ease-in-out;
}

.custom-btn.active:hover {
  background-position: left;
}

:deep(.v-btn.v-btn--disabled .v-icon) {
  color: inherit !important ;
  opacity: 1 !important;
}
</style>
